/**
 * FIRESTORE COLLECTIONS - Centralized collection name constants
 *
 * Single source of truth for all Firestore collection names used across the app.
 * Import from here instead of hardcoding strings in services.
 *
 * @example
 * import { Collections } from '@/core/config/collections';
 * db.collection(Collections.USERS).doc(userId).get();
 */

export const Collections = {
    // ============ USERS ============
    USERS: 'users',
    PROFILES: 'profiles',
    USER_NOTIFICATIONS: 'user_notifications',
    USER_PRACTICE_LIBRARY: 'userPracticeLibrary',
    USER_PRACTICE_FOLDERS: 'userPracticeFolders',

    // ============ THERAPIST ============
    THERAPIST_ACCOUNTS: 'therapistAccounts',
    THERAPIST_PATIENTS: 'therapistPatients',
    THERAPIST_PUBLIC_PROFILES: 'therapist_public_profiles',
    THERAPIST_NOTES: 'therapist_notes',
    THERAPIST_NOTE_CHUNKS: 'therapist_note_chunks',
    THERAPIST_SETTINGS: 'therapist_settings',
    THERAPIST_AVAILABILITY: 'therapist_availability',
    THERAPIST_BLOCKED_DATES: 'therapist_blocked_dates',
    THERAPIST_BRANDING: 'therapist_branding',
    THERAPIST_REVIEWS: 'therapist_reviews',
    THERAPIST_ARTICLES: 'therapist_articles',
    THERAPIST_AUTOMATIONS: 'therapist_automations',
    THERAPIST_TRANSACTIONS: 'therapist_transactions',
    THERAPIST_RECURRING_TRANSACTIONS: 'therapist_recurring_transactions',
    THERAPIST_FINANCIAL_CONFIG: 'therapist_financial_config',
    THERAPIST_MONTHLY_SUMMARIES: 'therapist_monthly_summaries',
    SESSION_NOTES: 'sessionNotes',
    AUTOMATION_CONVERSATIONS: 'automation_conversations',
    AUTOMATION_MESSAGES: 'automation_messages',
    AUTOMATION_TEMPLATES: 'automation_templates',

    // ============ PATIENT ============
    PATIENT_INVITES: 'patient_invites',
    PATIENT_CONSENTS: 'patient_consents',
    PATIENT_JOURNAL: 'patient_journal',
    PATIENT_PRACTICE_ASSIGNMENTS: 'patientPracticeAssignments',
    PATIENT_SCHEDULING_HISTORY: 'patient_scheduling_history',

    // ============ SCHEDULING ============
    APPOINTMENTS: 'appointments',
    BOOKING_REQUESTS: 'booking_requests',
    RECURRING_APPOINTMENTS: 'recurring_appointments',
    RESCHEDULING_POLICIES: 'rescheduling_policies',
    RESCHEDULE_REQUESTS: 'reschedule_requests',
    WAITLIST: 'waitlist',
    WAITLIST_CONFIG: 'waitlist_config',
    WAITLIST_NOTIFICATIONS: 'waitlist_notifications',

    // ============ AI ============
    AI_AGENTS: 'ai_agents',
    AI_USAGE_LOGS: 'ai_usage_logs',
    AI_ASSISTANT_CONFIGS: 'patientAIAssistantConfigs',
    AI_ASSISTANT_INTERACTIONS: 'aiAssistantInteractions',
    MATCHING_SESSIONS: 'matching_sessions',
    COMPATIBILITY_TESTS: 'compatibility_tests',

    // ============ CONTENT ============
    KNOWLEDGE_BASE: 'knowledge_base',
    KNOWLEDGE_DOCUMENTS: 'knowledge_documents',
    KNOWLEDGE_FOLDERS: 'knowledge_folders',
    DOCUMENT_GROUPS: 'document_groups',
    CONTENT_IDEAS_CACHE: 'content_ideas_cache',
    CONTENT_SUGGESTIONS: 'content_suggestions',
    GENERATED_MEDIA: 'generatedMedia',
    PRACTICES: 'practices',
    THERAPIST_PRACTICE_USAGE: 'therapist_practice_usage',

    // ============ CALENDAR ============
    USER_CALENDAR_TOKENS: 'user_calendar_tokens',
    USER_GCAL_SYNC: 'user_gcal_sync', // mapping assignmentId → gcalEventId

    // ============ TOOLS ============
    THERAPEUTIC_TOOLS: 'therapeutic_tools',
    TOOL_REQUESTS: 'toolRequests',
    TOOL_RATINGS: 'tool_ratings',
    MOOD_ENTRIES: 'mood_entries',
    WHEEL_OF_LIFE: 'wheelOfLifeEntries',

    // ============ SCHOOL ============
    SCHOOLS: 'schools',
    SCHOOL_MEMBERS: 'school_members',
    SCHOOL_PRACTICES: 'school_practices',
    SCHOOL_TAXONOMIES: 'school_taxonomies',
    SCHOOL_COURSES: 'school_courses',
    COURSE_LESSONS: 'course_lessons',
    COURSE_ENROLLMENTS: 'course_enrollments',

    // ============ MARKETING ============
    AFFILIATES: 'affiliates',
    REFERRED_USERS: 'referred_users',
    AFFILIATE_PAYOUTS: 'affiliate_payouts',
    AFFILIATE_COMMISSIONS: 'affiliate_commissions',
    CLAIMABLE_PROFILES: 'claimable_profiles',
    PUBLIC_QUESTIONS: 'public_questions',
    ADS_CAMPAIGNS: 'ads_campaigns',
    ADS_CREATIVES: 'ads_creatives',
    REFERRAL_BENEFITS: 'referral_benefits',

    // ============ OPERATIONS ============
    FEEDBACK_SUBMISSIONS: 'feedback_submissions',
    ERROR_GROUPS: 'error_groups',
    LOGS: 'logs',
    PROCESS_EXECUTIONS: 'process_executions',
    PROCESSING_JOBS: 'processing_jobs',
    NOTIFICATIONS: 'notifications',
    REVIEW_REPORTS: 'review_reports',
    FIREBASE_COSTS: 'firebase_costs',
    SYSTEM_CONFIG: 'system_config',

    // ============ COMMUNICATIONS ============
    MAIL: 'mail',
    EMAIL_QUEUE: 'email_queue',
} as const;

export type CollectionName = typeof Collections[keyof typeof Collections];
